import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  Tooltip,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import {
  Filter,
  X,
  Check,
  Calendar,
  BookOpen,
  Gavel,
  User,
  FileText,
  Hash,
  Zap,
} from "react-feather";
import Facet from "../UI/Facet";
import { applyFilters, searchResults } from "../../actions/searchActions";
import { buildCitationString } from "../../utils/HelperFunction/textFormatting";
import MaximsFilters from "../CaseSearch/MaximsFilters";
import LegalTermsFilters from "../CaseSearch/LegalTermsFilters";
import WordsAndPhrasesFilters from "../CaseSearch/WordsAndPhrasesFilters";

const transformFacets = (value) => {
  const transformed = [];

  if (value.Section) {
    transformed.push({
      category: "Journal",
      options: value.Section.map((section) => section.value),
    });
  }

  if (value.Court) {
    const uniqueCourt = new Map();

    value.Court.forEach((Court) => {
      const originalValue = Court.value && Court.value.trim();

      if (originalValue && originalValue.length > 0) {
        let formattedValue = originalValue
          .replace(/^[.,&]\s*/g, "")
          .replace(/\b\(\w+\)\b/g, " ")
          .trim()
          .replace(/\b\w+(-\w+)*\b/g, (word) =>
            word
              .split("-")
              .map(
                (part) =>
                  part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
              )
              .join(" "),
          )
          .replace(/\s{2,}/g, " ")
          .trim();

        if (
          formattedValue &&
          formattedValue.length > 4 &&
          !/^\d/.test(formattedValue) &&
          formattedValue !== "--"
        ) {
          uniqueCourt.set(formattedValue, originalValue);
        }
      }
    });

    transformed.push({
      category: "Court",
      options: Array.from(uniqueCourt.entries()).map(
        ([formatted, original]) => ({
          formatted,
          original,
        }),
      ),
    });
  }
  if (value.Year) {
    transformed.push({
      category: "Year",
      options: value.Year.map((year) => year.value),
    });
  }

  transformed.push({
    category: "Page",
    options: [],
  });

  if (value.Judge_Name) {
    const uniqueJudges = [];

    value.Judge_Name.forEach((judge) => {
      const originalValue = judge.value && judge.value.trim();

      if (originalValue && originalValue.length > 0) {
        let formattedValue = originalValue
          .replace(/\b(Honourable Justice|Honorable Justice|Justice)\b\s*/i, "")
          .replace(/^[.,&]\s*/g, "")
          .replace(/\b\(\w+\)\b/g, " ")
          .trim()
          .replace(/\b\w+(-\w+)*\b/g, (word) =>
            word
              .split("-")
              .map(
                (part) =>
                  part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
              )
              .join("-"),
          )
          .replace(/\s{2,}/g, " ")
          .trim();

        if (
          formattedValue &&
          formattedValue.length > 4 &&
          !/^\d/.test(formattedValue) &&
          formattedValue !== "--"
        ) {
          uniqueJudges.push({ formattedValue, originalValue });
        }
      }
    });

    transformed.push({
      category: "Judge",
      options: uniqueJudges.map(({ formattedValue, originalValue }) => ({
        formatted: formattedValue,
        original: originalValue,
      })),
    });
  }

  if (value.Lawyer_Name) {
    const uniqueLawyers = [];

    value.Lawyer_Name.forEach((lawyer) => {
      const originalValue = lawyer.value && lawyer.value.trim();

      if (originalValue && originalValue.length > 0) {
        let formattedValue = originalValue
          .replace(/\b(Honourable Justice|Honorable Justice|Justice)\b\s*/i, "")
          .replace(/^[.,&]\s*/g, "")
          .replace(/\b\(\w+\)\b/g, " ")
          .trim()
          .replace(/\b\w+(-\w+)*\b/g, (word) =>
            word
              .split("-")
              .map(
                (part) =>
                  part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
              )
              .join("-"),
          )
          .replace(/\s{2,}/g, " ")
          .trim();

        if (
          formattedValue &&
          formattedValue.length > 4 &&
          !/^\d/.test(formattedValue) &&
          formattedValue !== "--"
        ) {
          uniqueLawyers.push({ formattedValue, originalValue });
        }
      }
    });

    transformed.push({
      category: "Lawyer",
      options: uniqueLawyers.map(({ formattedValue, originalValue }) => ({
        formatted: formattedValue,
        original: originalValue,
      })),
    });
  }

  return transformed;
};

const LegalSearchSidebar = ({
  toggleSidebar,
  searchKeyword,
  sideBarState,
  currentPage,
  setCurrentPage,
  isNewCaseLawSearch,
  setIsNewCaseLawSearch,
  searchMode,
  resultsCount,
  activeTab,
}) => {
  const dispatch = useDispatch();

  const [startYear, setStartYear] = useState(null);
  const [endYear, setEndYear] = useState(null);
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const searchResultsState = useSelector((state) => state.searchResults);
  const { facets } = searchResultsState;

  const searchSelectedFilters = useSelector((state) => state.selectedFilters);
  const { selectedFilters } = searchSelectedFilters;

  const [selectedValues, setSelectedValues] = useState({});
  const [showChips, setShowChips] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [selectedFilterMode, setSelectedFilterMode] = useState("standard");
  const [facetValues, setFacetValues] = useState([]);

  // In citation mode, the filter is a single "{year} {journal} {page}"
  // citation string — all three fields are required before it is valid.
  const citationFilterValue =
    selectedFilterMode === "citation"
      ? buildCitationString(
          selectedValues?.["Year"],
          selectedValues?.["Journal"],
          selectedValues?.["Page"],
        )
      : null;
  const isCitationFilterIncomplete =
    selectedFilterMode === "citation" && !citationFilterValue;

  const buildFiltersToApply = () => {
    if (selectedFilterMode === "citation") {
      return citationFilterValue
        ? { Citation_Name: citationFilterValue }
        : null;
    }
    return {
      ...selectedValues,
      Judge: selectedValues?.["Judge"]?.original,
      Journal: selectedValues?.["Journal"],
      Court: selectedValues?.["Court"]?.original,
      Lawyer: selectedValues?.["Lawyer"]?.original,
    };
  };

  const handleApplyCitationFilter = () => {
    if (selectedValues) {
      const updatedFilters = buildFiltersToApply();
      if (!updatedFilters) {
        return;
      }
      dispatch(
        applyFilters(
          searchKeyword,
          updatedFilters,
          (currentPage - 1) * resultsCount,
          resultsCount,
          searchMode,
        ),
      );
      setIsFilterApplied(true);
      setShowChips(true);
      if (isMobileView) {
        toggleSidebar();
      }
    }
  };

  const handleClearFilters = () => {
    setSelectedValues({});
    setShowChips(false);
    setIsFilterApplied(false);
    setStartYear(null);
    setEndYear(null);
    dispatch({ type: "SEARCH_SELECTED_FILTERS_RESET" });
    dispatch(
      searchResults(
        searchKeyword,
        "",
        (currentPage - 1) * resultsCount,
        resultsCount,
        searchMode,
      ),
    );

    if (isMobileView) {
      toggleSidebar();
    }
  };

  const handleChange = (category, value) => {
    const updatedSelectedValues = { ...selectedValues };
    updatedSelectedValues[category] = value;
    setSelectedValues(updatedSelectedValues);

    dispatch({
      type: "SEARCH_SELECTED_FILTERS",
      payload: {
        updatedSelectedValues,
        updatedShowChips: showChips,
        selectedFilterMode,
      },
    });
  };

  const handleChangeFilterMode = (event, newAlignment) => {
    if (newAlignment !== null) {
      setSelectedFilterMode(newAlignment);
      dispatch({
        type: "SEARCH_SELECTED_FILTERS",
        payload: {
          updatedSelectedValues: selectedValues,
          updatedShowChips: showChips,
          selectedFilterMode: newAlignment,
        },
      });
    }
  };

  const handleClearChip = (category) => {
    const updatedSelectedValues = { ...selectedValues };
    delete updatedSelectedValues[category];
    setSelectedValues(updatedSelectedValues);

    dispatch({
      type: "SEARCH_SELECTED_FILTERS",
      payload: {
        updatedSelectedValues,
        updatedShowChips: showChips,
        selectedFilterMode,
      },
    });
  };

  useEffect(() => {
    if (facets) {
      const transformedFacets = transformFacets(facets);
      setFacetValues(transformedFacets);
    }
  }, [facets, searchResultsState]);

  useEffect(() => {
    if (isFilterApplied && !isMobileView) {
      const updatedFilters = buildFiltersToApply();
      if (updatedFilters) {
        dispatch(
          applyFilters(
            searchKeyword,
            updatedFilters,
            (currentPage - 1) * resultsCount,
            resultsCount,
            searchMode,
          ),
        );
      }
    }
  }, [currentPage, resultsCount]);

  useEffect(() => {
    if (selectedFilters) {
      setIsFilterApplied(true);
      setShowChips(selectedFilters.updatedShowChips);
      const updatedFilters = {
        ...selectedFilters.updatedSelectedValues,
        Judge: selectedFilters?.updatedSelectedValues?.["Judge"]?.original,
        Journal: selectedFilters?.updatedSelectedValues?.["Journal"],
        Court: selectedFilters?.updatedSelectedValues?.["Court"]?.original,
        Lawyer: selectedFilters?.updatedSelectedValues?.["Lawyer"]?.original,
      };
      setSelectedValues(selectedFilters.updatedSelectedValues);
      setStartYear(selectedFilters.updatedSelectedValues?.Year?.startYear);
      setEndYear(selectedFilters.updatedSelectedValues?.Year?.endYear);
      if (isMobileView && !sideBarState) {
        dispatch(
          applyFilters(
            searchKeyword,
            updatedFilters,
            (currentPage - 1) * resultsCount,
            resultsCount,
            searchMode,
          ),
        );
      } else if (!isMobileView) {
        dispatch(
          applyFilters(
            searchKeyword,
            updatedFilters,
            (currentPage - 1) * resultsCount,
            resultsCount,
            searchMode,
          ),
        );
      }
    }
    return () => {
      if (!isMobileView) {
        dispatch({ type: "SEARCH_SELECTED_FILTERS_RESET" });
      }
      setSelectedValues({});
      setStartYear(null);
      setEndYear(null);
    };
  }, []);

  // Get tab colors based on activeTab
  const getTabColors = (tabIndex) => {
    const colors = [
      { primary: "#0073A6", hover: "#005A8B" }, // Judgements - Blue
      { primary: "#F59E0B", hover: "#D97706" }, // Maxims - Amber
      { primary: "#10B981", hover: "#059669" }, // Terms - Green
      { primary: "#7C3AED", hover: "#6D28D9" }, // Words & Phrases - Purple
    ];
    return colors[tabIndex] || colors[0];
  };

  const tabColors = getTabColors(activeTab);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: isMobileView ? "300px" : "15%",
        height: "100%",
        }}
    >
      <Box
        sx={{
          display: "flex",
          boxSizing: "border-box",
          flexDirection: "column",
          p: "8px",
          borderRadius: "8px",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            paddingTop: "16px",
            // paddingRight: "16px",
            paddingBottom: "12px",
            // paddingLeft: "16px",
            borderBottom: "1px solid #F1F5F9",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px 8px 0 0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "40px",
                  height: "40px",
                  backgroundColor: `${tabColors.primary}15`,
                  borderRadius: "12px",
                  color: tabColors.primary,
                }}
              >
                <Filter size={20} />
              </Box>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#111827",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                Search Filters
              </Typography>
            </Box>
            {isMobileView && (
              <IconButton
                onClick={toggleSidebar}
                sx={{
                  color: "#6B7280",
                  "&:hover": {
                    backgroundColor: "#F3F4F6",
                  },
                }}
              >
                <X size={20} />
              </IconButton>
            )}
          </Box>

          {/* Filter Mode Toggle */}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={selectedFilterMode}
            onChange={handleChangeFilterMode}
            sx={{
              width: "100%",
              backgroundColor: "#F8FAFC",
              // borderRadius: "12px",
              // padding: "4px",
              "& .MuiToggleButtonGroup-grouped": {
                flex: 1,
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 500,
                color: "#6B7280",
                // border: "none",
                // borderRadius: "8px !important",
                // // padding: "8px 12px",
                height: "36px",
                transition: "all 0.2s ease-in-out",
                whiteSpace: "nowrap",
                flexWrap: "wrap",
                minWidth: 0,
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                },
                "&.Mui-selected": {
                  backgroundColor: "#FFFFFF",
                  color: tabColors.primary,
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    backgroundColor: "#FFFFFF",
                  },
                },
              },
            }}
          >
            <ToggleButton value="standard">
              <Box sx={{ display: "flex", alignItems: "center",  }}>
                <Hash size={14} />
                Standard
              </Box>
            </ToggleButton>
            <ToggleButton value="citation">
              <Box sx={{ display: "flex", alignItems: "center", }}>
                <FileText size={14} />
                Citation
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, padding: "1px", paddingTop: "8px" }}>
          {activeTab === 0 ? (
            <Facet
              facetsData={facetValues}
              handleChange={handleChange}
              selectedValues={selectedValues}
              showChips={showChips}
              handleClearChip={handleClearChip}
              filterMode={selectedFilterMode}
              startYear={startYear}
              setStartYear={setStartYear}
              endYear={endYear}
              setEndYear={setEndYear}
            />
          ) : activeTab === 1 ? (
            <MaximsFilters />
          ) : activeTab === 2 ? (
            <LegalTermsFilters />
          ) : (
            <WordsAndPhrasesFilters />
          )}
        </Box>

        {/* Footer Actions */}
        {activeTab === 0 && (
          <Box
            sx={{
              padding: "12px 16px 16px 16px",
              borderTop: "1px solid #F1F5F9",
              backgroundColor: "#FFFFFF",
              borderRadius: "0 0 8px 8px",
              marginTop: "auto",
            }}
          >
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<X size={16} />}
                onClick={handleClearFilters}
                sx={{
                  flex: 1,
                  textTransform: "none",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#EF4444",
                  borderColor: "#FECACA",
                  backgroundColor: "#FEF2F2",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  height: "36px",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#FEE2E2",
                    borderColor: "#FCA5A5",
                  },
                }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<Check size={16} />}
                disabled={
                  selectedFilterMode === "citation"
                    ? isCitationFilterIncomplete
                    : Object.keys(selectedValues)?.length === 0
                }
                onClick={handleApplyCitationFilter}
                sx={{
                  flex: 1,
                  textTransform: "none",
                  fontSize: "12px",
                  fontWeight: 500,
                  backgroundColor: tabColors.primary,
                  borderRadius: "8px",
                  padding: "8px 16px",
                  height: "36px",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: tabColors.hover,
                    boxShadow: `0 2px 8px ${tabColors.primary}30`,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#F3F4F6",
                    color: "#9CA3AF",
                  },
                }}
              >
                Apply
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LegalSearchSidebar;
